"use client";
import Image from "next/image";
import React, { useMemo } from "react";
import { TrustpilotMini } from "staysure-component-library";

import { useTrustpilotRating } from "@/app/components/cover-type/components/description-section/trust-pilot/trust-pilot.hook";
import withBrand from "@/components/with-brand";
import {
  BRAND_AVANTI,
  BRANDDOMAIN_AVN,
  BRANDDOMAIN_STS,
  TRUSTPILOT_AVN_URL,
  TRUSTPILOT_STS_URL,
} from "@/constants";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { BrandKey } from "@/types/common-types";

import SimpleSlider from "./carousel";

function InterstitialPageContent({
  brand = "staysure",
}: Readonly<{
  brand?: BrandKey;
}>) {
  const { t } = useWizardTranslation();
  const isSmallScreen = useMediaQuery("(max-width: 903px)");

  const isAvanti = brand === BRAND_AVANTI;

  const brandConfig = useMemo(
    () => ({
      domain: isAvanti ? BRANDDOMAIN_AVN : BRANDDOMAIN_STS,
      trustpilotUrl: isAvanti ? TRUSTPILOT_AVN_URL : TRUSTPILOT_STS_URL,
      animationPath: isAvanti
        ? "/images/renewals/renewals-landing-avn.gif"
        : "/images/renewals/renewals-landing-sts.gif",
      badges: isAvanti
        ? [
            {
              src: "/images/AVN Defaqto.png",
              alt: "Defaqto",
              width: 120,
              height: 95,
              className: "!h-[95px] !w-[120px] md:ml-[26px] ",
              description: "",
            },
          ]
        : [
            {
              src: "/images/best-buy.png",
              alt: "Best Buy",
              width: 118,
              height: 95,
              className: "mb-6 mx-auto",
              description:
                "Our Comprehensive and Signature travel insurance is a Which? Best Buy",
            },
            {
              src: "/images/BTA Award.png",
              alt: "BTA",
              width: 202,
              height: 95,
              className: "mb-6 mx-auto",
              description:
                "Awarded Best Company for Travel Insurance at the British Travel Awards 2016-2024",
            },
            {
              src: "/images/Defaqto2025.png",
              alt: "Defaqto",
              width: 113,
              height: 95,
              className: "mb-6 mx-auto",
              description:
                '“An excellent product with a comprehensive range of features and benefits" - Defaqto',
            },
          ],
    }),
    [isAvanti]
  );

  const reviewData = useTrustpilotRating(brandConfig.domain) ?? {};
  const ratingLabels = {
    poor: t("trustpilot_rating_poor", "Poor"),
    fair: t("trustpilot_rating_fair", "Fair"),
    good: t("trustpilot_rating_good", "Good"),
    veryGood: t("trustpilot_rating_very_good", "Very Good"),
    excellent: t("trustpilot_rating_excellent", "Excellent"),
  };

  return (
    <div className="md:pt-spacing-xl lg:pb-spacing-xxl md:pb-spacing-6xl px-spacing-m md:px-[0px] lg:min-h-[640px] md:min-h-[899px] min-h-[688px] md:flex md:items-start lg:block">
      <div className="w-full  h-fit">
        {(isAvanti || (!isAvanti && !isSmallScreen)) && (
          <div className="w-fit mx-auto h-[200px]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={brandConfig.animationPath}
              alt="landing-animation"
              className="w-[200px] h-[200px] text-transparent"
              role="presentation"
              aria-hidden="true"
            />
          </div>
        )}
        <p className="lg:my-spacing-xl md:mt-spacing-5xl md:mb-spacing-4xl mt-[96px] mb-spacing-xxl font-[400] md:leading-[45px] md:text-5xl leading-[29.9px] text-xl font-header-font text-dark-text-title text-center w-full whitespace-normal break-words px-4">
          Building your personalised quote
        </p>

        {isSmallScreen && !isAvanti && (
          <SimpleSlider
            trustpilotData={{
              stars: reviewData?.stars ?? 0,
              score: reviewData?.score ?? 0,
              totalReviews: reviewData?.totalReviews ?? "",
              link: brandConfig.trustpilotUrl,
              ratingLabels: ratingLabels,
              reviewsText: t("trustpilot_review_text", "reviews"),
            }}
            badges={brandConfig.badges}
          />
        )}

        {(isAvanti || (!isAvanti && !isSmallScreen)) && (
          <div className="w-[224px] md:w-full mx-auto flex items-center justify-center">
            <div
              className={`m-4 md:mx-auto w-fit flex flex-col md:flex-row  items-center justify-center ${
                isAvanti
                  ? "gap-spacing-m"
                  : "md:max-w-[700px] xl:max-w-none md:flex-wrap xl:flex-nowrap"
              }`}
            >
              <div
                className={`${
                  isAvanti
                    ? ""
                    : "max-w-[276px] md:w-[calc(50%-24px)] md:basis-[calc(50%-24px)] xl:w-auto xl:basis-auto px-[24px]"
                }`}
              >
                <div className="mb-6">
                  <TrustpilotMini
                    stars={reviewData?.stars ?? 0}
                    score={reviewData?.score ?? 0}
                    totalReviews={reviewData?.totalReviews ?? ""}
                    link={brandConfig.trustpilotUrl}
                    ratingLabels={ratingLabels}
                    reviewsText={t("trustpilot_review_text", "reviews")}
                  />
                </div>
                {!isAvanti && (
                  <p className="text-3xs leading-normal text-center">
                    We've got more 5 Star reviews on Trustpilot than any other
                    travel insurance provider
                  </p>
                )}
              </div>
              {brandConfig.badges.map((badge, index) => (
                <div
                  key={badge.alt}
                  className={`max-w-[276px] md:w-[calc(50%-24px)] md:basis-[calc(50%-24px)] xl:w-auto xl:basis-auto px-6 flex flex-col ${
                    index >= 1 ? "md:mt-4 xl:mt-0" : ""
                  }`}
                >
                  <Image
                    key={`${badge.src}-${index}`}
                    src={badge.src}
                    alt={badge.alt}
                    width={badge.width}
                    height={badge.height}
                    className={badge.className}
                    priority
                  />
                  {badge.description && (
                    <div>
                      <p className="text-3xs leading-normal text-center">
                        {badge.description}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default withBrand(InterstitialPageContent);
